import React from "react";
import classNames from "classnames";
import { Button, type ButtonProps } from "../button";
import { Icon } from "../icon";
import "./PickerSurface.scss";

/** Политика показа нижнего действия закрытия picker surface. */
export type PickerSurfaceCloseActionPolicy = "fullscreen" | "always" | "never";

/** Пропсы контейнера {@link PickerSurface}. */
export interface PickerSurfaceProps {
    /** Дополнительный CSS-класс корневой поверхности. */
    className?: string;
    /** Основное содержимое поверхности выбора. */
    children?: React.ReactNode;
}

/** Пропсы верхней области {@link PickerSurfaceControls}. */
export interface PickerSurfaceControlsProps {
    /** Дополнительный CSS-класс области контролов. */
    className?: string;
    /** Поиск, фильтры или другие контролы над списком. */
    children?: React.ReactNode;
}

/** Пропсы основной области {@link PickerSurfaceBody}. */
export interface PickerSurfaceBodyProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Дополнительный CSS-класс scroll/flex области. */
    className?: string;
    /** Список, empty/loading state или кастомное picker-содержимое. */
    children?: React.ReactNode;
    /**
     * Включить overflow-scroll на самой body-области. \
     * Отключайте, если вложенный список сам управляет скроллом.
     * @default false
     */
    scrollable?: boolean;
}

/** Пропсы нижней области {@link PickerSurfaceFooter}. */
export interface PickerSurfaceFooterProps {
    /** Дополнительный CSS-класс footer-области. */
    className?: string;
    /** Нижние действия поверхности выбора. */
    children?: React.ReactNode;
}

/** Пропсы кнопки закрытия {@link PickerSurfaceCloseButton}. */
export interface PickerSurfaceCloseButtonProps
    extends Pick<ButtonProps, "className" | "children" | "onClick" | "disabled" | "type"> {
    /**
     * Accessible label кнопки.
     * @default "Закрыть список выбора и вернуться к форме"
     */
    "aria-label"?: string;
}

/** Верхняя область picker surface для поиска и фильтров. */
function PickerSurfaceControls({ className, children }: PickerSurfaceControlsProps) {
    return <div className={classNames("shared_picker_surface__controls", className)}>{children}</div>;
}

/** Растягиваемая область picker surface для списка или кастомного контента. */
function PickerSurfaceBody({ className, children, scrollable = false, ...rest }: PickerSurfaceBodyProps) {
    return (
        <div
            className={classNames(
                "shared_picker_surface__body",
                { "shared_picker_surface__body--scrollable": scrollable },
                className,
            )}
            {...rest}
        >
            {children}
        </div>
    );
}

/** Нижняя область picker surface для основного действия или закрытия fullscreen picker. */
function PickerSurfaceFooter({ className, children }: PickerSurfaceFooterProps) {
    return <div className={classNames("shared_picker_surface__footer", className)}>{children}</div>;
}

/** Полноширинная кнопка возврата из fullscreen picker surface. */
function PickerSurfaceCloseButton({
    className,
    children = "Вернуться назад",
    type = "button",
    "aria-label": ariaLabel = "Закрыть список выбора и вернуться к форме",
    ...buttonProps
}: PickerSurfaceCloseButtonProps) {
    return (
        <Button
            className={classNames("shared_picker_surface__close_button", className)}
            type={type}
            variant="primary"
            startSlot={<Icon src="/icons/common/arrow-left.svg" size="md" />}
            aria-label={ariaLabel}
            {...buttonProps}
        >
            {children}
        </Button>
    );
}

type PickerSurfaceComponent = ((props: PickerSurfaceProps) => React.ReactElement) & {
    /** Верхняя область picker surface для поиска и фильтров. */
    Controls: typeof PickerSurfaceControls;
    /** Растягиваемая область picker surface для списка или кастомного контента. */
    Body: typeof PickerSurfaceBody;
    /** Нижняя область picker surface для основного действия или закрытия fullscreen picker. */
    Footer: typeof PickerSurfaceFooter;
    /** Полноширинная кнопка возврата из fullscreen picker surface. */
    CloseButton: typeof PickerSurfaceCloseButton;
};

/** Flex-layout для содержимого picker surface: controls, body и footer. */
export const PickerSurface: PickerSurfaceComponent = Object.assign(
    function PickerSurfaceRoot({ className, children }: PickerSurfaceProps) {
        return <div className={classNames("shared_picker_surface", className)}>{children}</div>;
    },
    {
        Controls: PickerSurfaceControls,
        Body: PickerSurfaceBody,
        Footer: PickerSurfaceFooter,
        CloseButton: PickerSurfaceCloseButton,
    },
);
